import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

function audienceRu(a) {
  if (a === 'platform') return 'Платформа';
  if (a === 'teacher_students') return 'Ученики преподавателя';
  if (a === 'personal') return 'Личный';
  return a;
}

function seriesLabel(tags) {
  if (!Array.isArray(tags) || !tags.length) return '';
  const series = tags.find(
    (t) =>
      t.type === 'collection' &&
      t.slug !== 'base_prog_for_ege' &&
      t.slug !== 'osnovy_python_oge',
  );
  return series?.name || tags[0]?.name || '';
}

export default function KonspektyListPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [seriesTags, setSeriesTags] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [searchDraft, setSearchDraft] = useState(() => searchParams.get('search') || '');

  const scope = searchParams.get('scope') || 'all';
  const search = (searchParams.get('search') || '').trim();
  const tag = (searchParams.get('tag') || '').trim();
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  const scopeOptions = useMemo(() => {
    if (!user) return [];
    if (user.is_student && !user.is_teacher) {
      return [
        { value: 'all', label: 'Все доступные' },
        { value: 'mine', label: 'Мои' },
        { value: 'from_teachers', label: 'Преподавателей' },
      ];
    }
    if (user.is_teacher || user.is_staff || user.is_admin) {
      return [
        { value: 'all', label: 'Все доступные' },
        { value: 'mine', label: 'Мои' },
        { value: 'from_students', label: 'Учеников' },
      ];
    }
    return [{ value: 'all', label: 'Все доступные' }, { value: 'mine', label: 'Мои' }];
  }, [user]);

  function patchParams(next) {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      if (value === undefined) return;
      if (value === '' || value === null || (value === 'all' && key === 'scope')) {
        params.delete(key);
      } else {
        params.set(key, String(value));
      }
    });
    if (next.scope !== undefined || next.search !== undefined || next.tag !== undefined) {
      if (next.page === undefined) params.delete('page');
    }
    if (params.get('page') === '1') params.delete('page');
    setSearchParams(params, { replace: true });
  }

  useEffect(() => {
    setSearchDraft(searchParams.get('search') || '');
  }, [searchParams]);

  useEffect(() => {
    const handle = setTimeout(() => {
      const next = searchDraft.trim();
      if (next === search) return;
      patchParams({ search: next });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // patchParams closes over searchParams; debounce only on draft
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft, search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const { data } = await api.get('/materials/konspekty/', {
          params: {
            scope,
            page,
            page_size: PAGE_SIZE,
            ...(search ? { search } : {}),
            ...(tag ? { tag } : {}),
          },
        });
        const list = data.results ?? data;
        if (!cancelled) {
          setItems(Array.isArray(list) ? list : []);
          setTotalCount(typeof data.count === 'number' ? data.count : (Array.isArray(list) ? list.length : 0));
          setSeriesTags(Array.isArray(data.series_tags) ? data.series_tags : []);
        }
      } catch (e) {
        if (!cancelled && e.response?.status === 404 && page > 1) {
          patchParams({ page: 1 });
          return;
        }
        if (!cancelled) setErr(e.response?.data?.detail || 'Не удалось загрузить конспекты');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scope, search, tag, page]);

  const canCreate =
    user && (user.is_student || user.is_teacher || user.is_staff || user.is_admin);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const hasFilters = Boolean(search || tag);
  const emptyText = hasFilters ? 'Ничего не найдено.' : 'Пока нет конспектов.';

  return (
    <div className="page konspekty-list-page">
      <div className="page-header konspekty-list-header">
        <h1>Конспекты</h1>
        {canCreate && (
          <Link to="/konspekty/new" className="btn btn-primary">
            Новый конспект
          </Link>
        )}
      </div>

      <div className="konspekty-scope-bar">
        {scopeOptions.map((o) => (
          <button
            key={o.value}
            type="button"
            className={scope === o.value ? 'konspekty-scope-btn active' : 'konspekty-scope-btn'}
            onClick={() => patchParams({ scope: o.value })}
          >
            {o.label}
          </button>
        ))}
      </div>

      <div className="konspekty-filters">
        <input
          className="form-input konspekty-search"
          type="search"
          placeholder="Поиск по названию"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          aria-label="Поиск по названию"
        />
        {seriesTags.length > 0 && (
          <div className="konspekty-series-bar" role="group" aria-label="Серии">
            <button
              type="button"
              className={!tag ? 'konspekty-scope-btn active' : 'konspekty-scope-btn'}
              onClick={() => patchParams({ tag: '' })}
            >
              Все серии
            </button>
            {seriesTags.map((t) => (
              <button
                key={t.slug}
                type="button"
                className={tag === t.slug ? 'konspekty-scope-btn active' : 'konspekty-scope-btn'}
                onClick={() => patchParams({ tag: tag === t.slug ? '' : t.slug })}
              >
                {t.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && <p className="muted">Загрузка…</p>}
      {err && <p className="form-error">{err}</p>}

      {!loading && !err && (
        <p className="konspekty-count muted">
          {totalCount
            ? `Показано ${items.length} из ${totalCount}`
            : emptyText}
        </p>
      )}

      {!loading && !items.length && !err ? null : (
        <ul className="konspekty-cards">
          {items.map((k) => {
            const series = seriesLabel(k.tags);
            return (
            <li key={k.id} className="konspekty-card">
              <Link to={`/konspekty/${encodeURIComponent(k.slug)}`} className="konspekty-card-title">
                {k.title}
              </Link>
              <div className="konspekty-card-meta">
                {series && <span>{series} · </span>}
                <span>{audienceRu(k.audience)}</span>
                {k.author_username && <span> · {k.author_username}</span>}
                <span> · просмотров: {k.views_count ?? 0}</span>
              </div>
            </li>
            );
          })}
        </ul>
      )}

      {!loading && !err && totalPages > 1 && (
        <div className="konspekty-pagination">
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page <= 1}
            onClick={() => patchParams({ page: page - 1 })}
          >
            Назад
          </button>
          <span>
            Стр. {page} из {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page >= totalPages}
            onClick={() => patchParams({ page: page + 1 })}
          >
            Вперёд
          </button>
        </div>
      )}
    </div>
  );
}
